import { Form, Select, DatePicker, Alert, Tooltip, Space } from "antd";
import {
  InfoCircleOutlined,
  WalletOutlined,
  CrownOutlined,
} from "@ant-design/icons";
import { WizardStep } from "../WizardStep";
import { ChoiceCardGroup } from "../ChoiceCardGroup";
import { ShippingContainerIcon, LclBoxesIcon } from "../ShippingContainerIcon";
import { CargoShipIcon, PlaneIcon } from "../TransportModeIcons";
import { useBookingWizard } from "../../../contexts/BookingWizardContext";
import { useTransportTypes } from "../../../hooks/useTransportTypes";
import { useBookingTypes } from "../../../hooks/useBookingTypes";
import { useIncoterms } from "../../../hooks/useIncoterms";
import dayjs from "dayjs";
import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  isFclBookingType,
  SERVICE_LEVEL_VALUES,
} from "../../../utils/serviceLevelUtils";

const bookingTypeIcon = (name: string) => {
  const key = name.trim().toUpperCase();
  if (key.includes("FCL")) return <ShippingContainerIcon />;
  return <LclBoxesIcon />;
};

const transportTypeIcon = (abbreviation: string, name: string) => {
  const key = `${abbreviation} ${name}`.toUpperCase();
  if (key.includes("AIR")) return <PlaneIcon />;
  return <CargoShipIcon />;
};

export const TransportDetails = () => {
  const { state, updateStep } = useBookingWizard();
  const { transportTypes, loading: loadingTransportTypes } =
    useTransportTypes();
  const { bookingTypes, loading: loadingBookingTypes } = useBookingTypes();
  const { incoterms, loading: loadingIncoterms } = useIncoterms();
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [selectedTransportType, setSelectedTransportType] = useState<
    number | undefined
  >(state.transport_type);
  const [selectedIncoterm, setSelectedIncoterm] = useState<number | undefined>(
    state.booking_incoterm
  );
  const [previousTransportType, setPreviousTransportType] = useState<
    number | undefined
  >(state.transport_type);

  const watchedBookingType = Form.useWatch("booking_type", form);
  const isFcl = isFclBookingType(watchedBookingType);

  const transportTypeOptions = useMemo(
    () =>
      transportTypes.map((type) => ({
        value: type.id,
        label: type.name,
        icon: transportTypeIcon(type.abbreviation, type.name),
      })),
    [transportTypes]
  );

  const bookingTypeOptions = useMemo(
    () =>
      bookingTypes.map((type) => ({
        value: type.id,
        label: type.name,
        icon: bookingTypeIcon(type.name),
      })),
    [bookingTypes]
  );

  const serviceLevelOptions = useMemo(
    () =>
      SERVICE_LEVEL_VALUES.map((value) => ({
        value,
        label: t(
          `bookingWizard.transportDetails.serviceLevel${value === "budget" ? "Budget" : "Premium"}`
        ),
        description: t(
          `bookingWizard.transportDetails.serviceLevel${value === "budget" ? "Budget" : "Premium"}Hint`
        ),
        icon:
          value === "budget" ? <WalletOutlined /> : <CrownOutlined />,
      })),
    [t]
  );

  // Reset booking_type when transport type changes to non-Seafreight
  useEffect(() => {
    // Only clear if the transport type has actually changed from Seafreight to something else
    if (previousTransportType === 2 && selectedTransportType !== 2) {
      form.setFieldValue("booking_type", undefined);
      form.setFieldValue("service_level", undefined);
    }

    // Update previous transport type for future comparisons
    setPreviousTransportType(selectedTransportType);
  }, [selectedTransportType, form, previousTransportType]);

  // Clear service_level only when booking type is explicitly non-FCL (not while loading)
  useEffect(() => {
    if (
      watchedBookingType != null &&
      !isFclBookingType(watchedBookingType)
    ) {
      form.setFieldValue("service_level", undefined);
    }
  }, [watchedBookingType, form]);

  // Check if EXW incoterm is selected (ID: 1)
  const isExwIncoterm = selectedIncoterm === 1;

  const handleNext = async () => {
    try {
      const values = await form.validateFields();

      // Create a copy of the form values for any necessary modifications
      const updatedValues = {
        ...values,
      };

      // If it's FOB (ID: 3), make sure includeExportClearanceLicense is false
      if (values.booking_incoterm === 3) {
        updatedValues.includeExportClearanceLicense = false;
      }

      // If transport type is not Seafreight (ID: 2), explicitly set booking_type to null
      if (values.transport_type !== 2) {
        updatedValues.booking_type = null;
        updatedValues.service_level = null;
      } else if (!isFclBookingType(values.booking_type)) {
        updatedValues.service_level = null;
      }

      await updateStep(1, updatedValues);
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const handleTransportTypeChange = (value: number) => {
    setSelectedTransportType(value);

    // If changing from Seafreight to something else, clear booking_type immediately
    if (value !== 2) {
      form.setFieldValue("booking_type", undefined);
      form.setFieldValue("service_level", undefined);
    }
  };

  const handleIncotermChange = (value: number) => {
    setSelectedIncoterm(value);
  };

  // Check if Seafreight is selected (ID: 2)
  const isSeafreight = selectedTransportType === 2;

  return (
    <WizardStep
      onNext={handleNext}
      onPrevious={() => updateStep(state.currentStep - 1, {})}
    >
      <Form form={form} layout="vertical" initialValues={state}>
        <Form.Item
          label={t("bookingWizard.transportDetails.transportType")}
          name="transport_type"
          rules={[
            {
              required: true,
              message: t(
                "bookingWizard.transportDetails.transportTypeRequired"
              ),
            },
          ]}
        >
          <ChoiceCardGroup
            disabled={loadingTransportTypes}
            options={transportTypeOptions}
            onSelect={handleTransportTypeChange}
          />
        </Form.Item>

        {isSeafreight && (
          <Form.Item
            label={t("bookingWizard.transportDetails.bookingType")}
            name="booking_type"
            rules={[
              {
                required: isSeafreight,
                message: t(
                  "bookingWizard.transportDetails.bookingTypeRequired"
                ),
              },
            ]}
          >
            <ChoiceCardGroup
              disabled={loadingBookingTypes}
              options={bookingTypeOptions}
            />
          </Form.Item>
        )}

        {isSeafreight && isFcl && (
          <Form.Item
            label={
              <Space size={6}>
                {t("bookingWizard.transportDetails.serviceLevel")}
                <Tooltip
                  title={t("bookingWizard.transportDetails.serviceLevelInfo")}
                >
                  <InfoCircleOutlined style={{ color: "#1890ff" }} />
                </Tooltip>
              </Space>
            }
            name="service_level"
            rules={[
              {
                required: true,
                message: t(
                  "bookingWizard.transportDetails.serviceLevelRequired"
                ),
              },
            ]}
          >
            <ChoiceCardGroup options={serviceLevelOptions} />
          </Form.Item>
        )}

        <Form.Item
          label={t("bookingWizard.transportDetails.incoterm")}
          name="booking_incoterm"
          rules={[
            {
              required: true,
              message: t("bookingWizard.transportDetails.incotermRequired"),
            },
          ]}
        >
          <Select
            placeholder={t("bookingWizard.transportDetails.selectIncoterm")}
            loading={loadingIncoterms}
            options={incoterms.map((incoterm) => ({
              value: incoterm.id,
              label: incoterm.name,
            }))}
            onChange={handleIncotermChange}
          />
        </Form.Item>

        {isExwIncoterm && (
          <Alert
            message={t("bookingWizard.transportDetails.exwIncotermSelected")}
            description={t(
              "bookingWizard.transportDetails.exwIncotermExplanation"
            )}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Form.Item
          label={t("bookingWizard.transportDetails.cargoReadinessDate")}
          name="origin_pickup_date"
          rules={[
            {
              required: true,
              message: t(
                "bookingWizard.transportDetails.cargoReadinessRequired"
              ),
            },
          ]}
          getValueProps={(value) => ({
            value: value ? dayjs(value) : undefined,
          })}
        >
          <DatePicker style={{ width: "100%" }} />
        </Form.Item>
      </Form>
    </WizardStep>
  );
};
